// Loader for cms_template rows in the governance Supabase. Read by
// the CMS root onCreate to hydrate the sidebar and initial state from
// a named template ('default', 'blog', 'portfolio', or a custom per-org
// slug).
//
// A template shape:
//   {
//     slug, name, org_id,
//     route_map: { '/path': { _section, _kind, ... }, ... },
//     default_state: { <section>: <value>, ... },
//     lang_schema, table_bindings, rich_text_paths, version
//   }
//
// `route_map` keys drive the sidebar; `default_state` seeds projectState
// on first mount. A `cms_preset` row can layer state_overrides on top.

import { getSupabaseAsync } from '../governanceBridge.js'

const _cache = new Map()

const _cacheKey = (orgId, slug) => `${orgId || 'default'}::${slug || 'default'}`

export const loadCmsTemplate = async function loadCmsTemplate(orgId, slug) {
  const key = _cacheKey(orgId, slug)
  if (_cache.has(key)) return _cache.get(key)

  // TODO[sdk-cms]: cms_template/cms_preset are governance-tier config
  // tables (cross-org templates). They don't fit the workspace-project
  // tenant scoping model — adding a workspaceProject.documents.cms route
  // would force tenant-scoped reads on what is effectively shared
  // template metadata. Once a /governance/cms-templates wrapper or a
  // dedicated cms.* SDK service exists, swap the direct supabase call.
  const sb = await getSupabaseAsync()
  if (!sb) return null

  const { data, error } = await sb
    .from('cms_template')
    .select('*')
    .eq('org_id', orgId || 'default')
    .eq('slug', slug || 'default')
    .maybeSingle()

  if (error || !data) {
    // Fallback: try the 'default' org for this slug — useful during
    // local dev before org-scoped templates are seeded.
    if ((orgId || 'default') !== 'default') {
      return loadCmsTemplate('default', slug)
    }
    return null
  }

  _cache.set(key, data)
  return data
}

export const listCmsTemplates = async function listCmsTemplates(orgId) {
  // TODO[sdk-cms]: see loadCmsTemplate for rationale.
  const sb = await getSupabaseAsync()
  if (!sb) return []
  const { data } = await sb
    .from('cms_template')
    .select('slug, name, version, updated_at')
    .eq('org_id', orgId || 'default')
    .order('updated_at', { ascending: false })
  return data || []
}

// Hydrate root state with a template's route_map + default_state, and
// record which slug is active. Idempotent — won't overwrite an already
// hydrated projectState unless `force` is true.
export const hydrateCmsTemplate = async function hydrateCmsTemplate(root, { orgId, slug, force } = {}) {
  if (!root) return null
  const targetSlug = slug || root?.projectSlug || 'default'
  const targetOrg = orgId || root?.jwtClaims?.workspace_org_id || 'default'
  const tpl = await loadCmsTemplate(targetOrg, targetSlug)
  if (!tpl) return null

  // A template defines the whole CMS shape; when it loads it owns
  // projectState + projectLang. The previous slug's data is discarded
  // on switch — drafts should be persisted through cms_preset rows
  // before nav.
  const prevSlug = root.projectSlug
  const slugChanged = prevSlug !== tpl.slug
  // First hydrate on this root — there's no prior template yet, so
  // the default state in state.js is a placeholder; always prefer the
  // template's defaults over it on initial load.
  const firstHydrate = !root.projectTemplate
  const resetData = force || slugChanged || firstHydrate || !root.projectState
  // Deep-clone template slices before handing to root.update. State
  // proxies retain references to template objects otherwise, producing
  // phantom keys on later reads.
  const clone = (v) => (v == null ? v : structuredClone(v))
  root.update({
    projectSlug: tpl.slug,
    projectTemplate: tpl,
    projectState: resetData ? clone(tpl.default_state) || {} : root.projectState,
    projectLang: resetData ? clone(tpl.lang_schema?.strings) || {} : root.projectLang,
    activeRoute: slugChanged ? '/' : root.activeRoute
  })
  return tpl
}

// Shallow-merge `value` into `root[topKey][nestedKey][innerKey]`,
// spreading both levels so the state proxy sees a new reference.
// If the nested slice is an array, innerKey is coerced to an index.
// Shared by setLangValue and per-cell mutators in StateContent.
export const mergeIntoNested = function mergeIntoNested(root, topKey, nestedKey, innerKey, value) {
  if (!root || !topKey || nestedKey == null || innerKey == null) return
  const top = root[topKey] || {}
  const nested = top[nestedKey]
  const nextNested = Array.isArray(nested)
    ? Object.assign([...nested], { [Number(innerKey)]: value })
    : { ...(nested || {}), [innerKey]: value }
  root.update({
    [topKey]: { ...top, [nestedKey]: nextNested }
  })
}

// Fills empty non-default locales for a key by copying the default
// locale's text through a translator. Stubbed as a prefix marker for
// MVP — swap for an sdk-bridge AI translate call when available.
export const translateLangKey = async function translateLangKey(root, keyName) {
  const schema = root?.projectTemplate?.lang_schema
  if (!schema || !keyName) return { ok: false, error: 'no schema or key' }
  const defaultLocale = schema.default || (schema.locales && schema.locales[0]) || 'en'
  const source = root?.projectLang?.[defaultLocale]?.[keyName]
  if (!source) return { ok: false, error: 'no source text' }

  const nextLang = { ...(root.projectLang || {}) }
  let filled = 0
  for (const code of schema.locales || []) {
    if (code === defaultLocale) continue
    const existing = nextLang[code]?.[keyName]
    if (existing && existing.length > 0) continue
    // Stub: mark with locale prefix. Real integration swaps this
    // for a sdk-bridge AI call.
    nextLang[code] = { ...(nextLang[code] || {}), [keyName]: `[${code.toUpperCase()}] ${source}` }
    filled += 1
  }
  if (filled > 0) root.update({ projectLang: nextLang })
  return { ok: true, filled }
}

export const setLangValue = function setLangValue(root, code, keyName, value) {
  mergeIntoNested(root, 'projectLang', code, keyName, value)
}

export const listCmsPresets = async function listCmsPresets(orgId, templateSlug) {
  // TODO[sdk-cms]: see loadCmsTemplate for rationale.
  const sb = await getSupabaseAsync()
  if (!sb) return []
  const { data } = await sb
    .from('cms_preset')
    .select('*')
    .eq('org_id', orgId || 'default')
    .eq('template_slug', templateSlug)
    .order('updated_at', { ascending: false })
  return data || []
}
